import React, { useEffect, useMemo, useState } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  getDefaultAvatarUrl,
  resolveAvatarSrc,
} from "@/lib/avatar";
import { getUserInitials } from "@/lib/user-initials";
import { cn } from "@/lib/utils";

export type AvatarSize = "sm" | "md" | "lg" | "xl";

export const AVATAR_SIZE_CLASSES: Record<AvatarSize, string> = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
  xl: "h-16 w-16",
};

const FALLBACK_TEXT_CLASSES: Record<AvatarSize, string> = {
  sm: "text-[10px]",
  md: "text-xs",
  lg: "text-sm",
  xl: "text-base",
};

type AvatarLoadStage = "primary" | "default" | "initials";

export interface PersonAvatarProps {
  userId?: string;
  userName?: string;
  photoFilename?: string | null;
  avatarUrl?: string | null;
  size?: AvatarSize;
  className?: string;
  showFallback?: boolean;
  isLoading?: boolean;
  /** Accessible label — defaults to a name-based label in Portuguese */
  ariaLabel?: string;
  /** When true, the avatar is decorative (parent provides the accessible name) */
  decorative?: boolean;
}

/**
 * Displays a person's avatar with profile photo, generated default, and initials fallback.
 */
export const PersonAvatar: React.FC<PersonAvatarProps> = ({
  userId,
  userName,
  photoFilename,
  avatarUrl,
  size = "md",
  className,
  showFallback = true,
  isLoading = false,
  ariaLabel,
  decorative = false,
}) => {
  const primaryAvatarUrl = useMemo(
    () =>
      resolveAvatarSrc({
        avatarUrl,
        photoFilename,
        userId,
      }),
    [avatarUrl, photoFilename, userId],
  );

  const defaultAvatarUrl = useMemo(
    () => getDefaultAvatarUrl(userId),
    [userId],
  );

  const [loadStage, setLoadStage] = useState<AvatarLoadStage>("primary");

  useEffect(() => {
    setLoadStage("primary");
  }, [primaryAvatarUrl]);

  const imageSrc =
    loadStage === "primary"
      ? primaryAvatarUrl
      : loadStage === "default"
        ? defaultAvatarUrl
        : undefined;

  const handleImageError = () => {
    setLoadStage((stage) => {
      if (stage === "primary" && primaryAvatarUrl !== defaultAvatarUrl) {
        return "default";
      }
      return "initials";
    });
  };

  const initials = getUserInitials(userName);
  const label =
    ariaLabel ??
    (userName ? `Avatar de ${userName}` : "Avatar do usuário");

  return (
    <Avatar
      className={cn(AVATAR_SIZE_CLASSES[size], className)}
      role={decorative ? "presentation" : "img"}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative ? true : undefined}
    >
      {imageSrc && (
        <AvatarImage
          src={imageSrc}
          alt=""
          aria-hidden="true"
          decoding="async"
          loading="lazy"
          className={cn(isLoading && "opacity-60")}
          onError={handleImageError}
        />
      )}

      {showFallback && (
        <AvatarFallback
          className={cn(
            "font-semibold select-none",
            FALLBACK_TEXT_CLASSES[size],
            isLoading && loadStage !== "initials" && "animate-pulse",
          )}
        >
          {initials}
        </AvatarFallback>
      )}
    </Avatar>
  );
};
